import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { capitalize } from 'lodash';
import { ApolloError } from '@apollo/client';
import Lozenge from '@atlaskit/lozenge';
import { Text } from '@atlaskit/primitives';
import { NEW_DATE_FORMAT } from '@atlassian/cssxp-typescommon';
import { DEFAULT_PAGE, DEFAULT_ROWS_PER_PAGE } from '@atlassian/cssxp-utils-entitlement';
import {
  errorStateProp,
  emptyStateProp,
  getSelectedColumns,
  NoData,
  TableHeading,
  SoldToAddress,
} from '@css-xp/ppc-common';
import { generatedTypesNew } from '@css-xp/graphtypes';
import { DynamicResultTable, FormattedDate, HeadCell, CopyableText } from '@css-xp/base';
import { PaymentMethodTypeV2 } from '@css-xp/graphtypes/src/generated-types-new';
import { formatPaymentMethod, getInvoiceGroupName, getTaxContent } from '@css-xp/billing-v2';
import { PaypalPaymentMethodInTable } from '@css-xp/ppc-paypal';
import { routePaths, defaultBillingProfileSortOptions, BillingProfileTableKeys } from '@css-xp/ppc-constants';
import { getRouteUrl } from '@css-xp/ppc-utils';
import { SortDirection } from '@css-xp/ppc-graphtypes';
import { EndCustomerContacts } from '@css-xp/contact-resources';
import { emptyStatePropForWrongSearch } from '@css-xp/ppc-common/src/state-props';
import { useFetchBillingProfiles } from './hooks/use-fetch-billing-profiles';
import './billing-profile-table.scss';

type Props = {
  transactionAccountId: string;
  invoiceGroupIds?: string[];
  label?: string;
  parentDataLoading?: boolean;
  parentError?: ApolloError | undefined;
  filterOptions?: generatedTypesNew.Maybe<generatedTypesNew.FilterOption>[] | generatedTypesNew.FilterOption | null;
  testIdSuffix?: string;
  shouldHideInnerHeading?: boolean;
  isDesignIteration1Enabled?: boolean;
  columnsToShow?: string[];
};

type ColumnList = HeadCell;

type HeaderMapType = {
  [key: string]: {
    name: TableColumns;
    content: string;
    key?: string;
    isChecked?: boolean;
    isMandatory?: boolean;
    isSortable?: boolean;
    width?: number;
  };
};

enum TableColumns {
  BILLING_PROFILE = 'Billing profile',
  SOLD_TO_ADDRESS = 'Sold-to address',
  PAYMENT_METHOD = 'Payment method',
  CUSTOMER_CONTACTS = 'Customer contacts',
  CURRENCY = 'Currency',
  CREATED_ON = 'Created on',
  TAX_ID = 'Tax ID',
  PRICE_ELIGIBILITY = 'Price eligibility',
  NUMBER = 'Number',
}

const headersMap = (isDesignIteration1Enabled?: boolean): HeaderMapType => {
  return {
    [TableColumns.BILLING_PROFILE]: {
      name: TableColumns.BILLING_PROFILE,
      key: BillingProfileTableKeys.NAME,
      isSortable: true,
      content: isDesignIteration1Enabled ? 'Billing profile' : 'Name',
      width: isDesignIteration1Enabled ? 20 : 15,
    },
    [TableColumns.SOLD_TO_ADDRESS]: {
      name: TableColumns.SOLD_TO_ADDRESS,
      content: TableColumns.SOLD_TO_ADDRESS,
      width: isDesignIteration1Enabled ? 35 : 20,
    },
    [TableColumns.PAYMENT_METHOD]: {
      name: TableColumns.PAYMENT_METHOD,
      content: TableColumns.PAYMENT_METHOD,
      width: 20,
    },
    [TableColumns.CUSTOMER_CONTACTS]: {
      name: TableColumns.CUSTOMER_CONTACTS,
      content: TableColumns.CUSTOMER_CONTACTS,
      width: isDesignIteration1Enabled ? 40 : 20,
    },
    [TableColumns.CURRENCY]: {
      name: TableColumns.CURRENCY,
      content: TableColumns.CURRENCY,
      width: 10,
    },
    [TableColumns.CREATED_ON]: {
      name: TableColumns.CREATED_ON,
      key: 'created-on',
      content: TableColumns.CREATED_ON,
      isMandatory: false,
      isChecked: true,
      width: 10,
    },
    [TableColumns.TAX_ID]: {
      name: TableColumns.TAX_ID,
      content: TableColumns.TAX_ID,
      isMandatory: false,
      isChecked: true,
      width: 15,
    },
    [TableColumns.PRICE_ELIGIBILITY]: {
      name: TableColumns.PRICE_ELIGIBILITY,
      content: TableColumns.PRICE_ELIGIBILITY,
      width: 10,
    },
    [TableColumns.NUMBER]: {
      name: TableColumns.NUMBER,
      content: TableColumns.NUMBER,
      width: 15,
    },
  };
};

// default columns to show for design iteration 1
const defaultColumns = [
  TableColumns.BILLING_PROFILE,
  TableColumns.SOLD_TO_ADDRESS,
  TableColumns.PAYMENT_METHOD,
  TableColumns.CURRENCY,
  TableColumns.CUSTOMER_CONTACTS,
];

export const SearchBillingProfilesTable = ({
  transactionAccountId,
  invoiceGroupIds,
  label,
  parentDataLoading,
  parentError,
  filterOptions,
  testIdSuffix = '',
  shouldHideInnerHeading,
  isDesignIteration1Enabled,
  columnsToShow = defaultColumns,
}: Props) => {
  const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [sortOrder, setSortOrder] = useState<SortDirection>(SortDirection.ASC);

  const { refetch, data, error, loading } = useFetchBillingProfiles({
    invoiceGroupIds,
    transactionAccountId: transactionAccountId,
    sortOptions: defaultBillingProfileSortOptions,
    filterOptions,
  });

  const onPageChange = (newPage: number) => {
    if (currentPage === newPage) {
      return;
    }
    setCurrentPage(newPage);
    refetch({
      id: transactionAccountId,
      searchOptions: {
        offset: newPage !== 1 ? newPage * rowsPerPage - rowsPerPage : 0,
        first: rowsPerPage,
        sort: defaultBillingProfileSortOptions,
      },
    });
  };

  const onRowPerPageChange = (newRowPerPage: number) => {
    if (newRowPerPage === rowsPerPage) {
      return;
    }
    setCurrentPage(1);
    setRowsPerPage(newRowPerPage);
    refetch({
      id: transactionAccountId,
      searchOptions: {
        offset: 0,
        first: newRowPerPage,
        sort: defaultBillingProfileSortOptions,
      },
    });
  };

  // variables to use for paging and populating our template
  const totalCount = data?.transactionAccount?.billingProfiles?.totalCount ?? 0;
  const results = data?.transactionAccount?.billingProfiles?.nodes ?? [];

  const showPagination = totalCount > DEFAULT_ROWS_PER_PAGE;

  const wrapInCell = (node: React.ReactElement | string) => {
    return <div>{node}</div>;
  };

  const getPayByStatementValue = useMemo(() => {
    return (billingProfile: generatedTypesNew.InvoiceGroup | undefined | null): boolean => {
      const paymentMethod = billingProfile?.defaultPaymentMethod?.type;
      if (paymentMethod === generatedTypesNew.PaymentMethodType.CARD) return false;
      if (paymentMethod === generatedTypesNew.PaymentMethodType.DEFERRED) {
        const deferredPaymentMethod = billingProfile?.defaultPaymentMethod as generatedTypesNew.PaymentOnTerms;
        return !!deferredPaymentMethod?.terms?.payOnStatement;
      }
      return false;
    };
  }, []);

  const onSort = () => {
    const newSortedData = [...tableRows].reverse();
    setTableRows(newSortedData);
    setSortOrder(sortOrder === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC);
  };

  const getHead = useMemo(() => {
    const columnHeaders = headersMap(isDesignIteration1Enabled);

    if (isDesignIteration1Enabled) {
      const updatedColumns = columnsToShow.map((column) => columnHeaders[column]);
      return updatedColumns;
    }

    const allColumns = [
      columnHeaders[TableColumns.BILLING_PROFILE],
      columnHeaders[TableColumns.SOLD_TO_ADDRESS],
      columnHeaders[TableColumns.PAYMENT_METHOD],
      columnHeaders[TableColumns.CUSTOMER_CONTACTS],
      columnHeaders[TableColumns.CURRENCY],
      columnHeaders[TableColumns.CREATED_ON],
      columnHeaders[TableColumns.TAX_ID],
      columnHeaders[TableColumns.PRICE_ELIGIBILITY],
    ];

    // adds the number column at the index 8 if isBillingProfileNumberEnabled is true
    allColumns.splice(8, 0, columnHeaders[TableColumns.NUMBER]);

    return allColumns;
  }, [columnsToShow, isDesignIteration1Enabled]);

  useEffect(() => {
    setTableRows(initialRows);
  }, [data?.transactionAccount?.billingProfiles?.nodes]);

  const [columnsList] = useState<Array<ColumnList>>(getHead);

  //@ts-ignore
  const rowData = (columns, billingProfile) => {
    return columns?.map((column: any) => {
      const hasPayOnStatement = getPayByStatementValue(billingProfile);
      //@ts-ignore
      const hasPayPalPaymentMethod = billingProfile?.defaultPaymentMethod?.type === PaymentMethodTypeV2.PAYPAL;

      switch (column.name) {
        case TableColumns.BILLING_PROFILE:
          return {
            key: BillingProfileTableKeys.NAME,
            content: wrapInCell(
              billingProfile?.id ? (
                <>
                  <Link
                    to={`${getRouteUrl(routePaths.billingProfileDetails, {
                      txaId: transactionAccountId,
                      billingProfileId: `${encodeURIComponent(billingProfile?.id ?? '')}`,
                    })}`}
                    onClick={(e) => e.stopPropagation()}
                    className="billing-profile-name"
                    data-testid="billing-profile-name-link"
                  >
                    {getInvoiceGroupName(billingProfile?.name)}
                    {billingProfile?.defaultPaymentMethod?.paymentIssue &&
                      billingProfile?.defaultPaymentMethod?.type !== generatedTypesNew.PaymentMethodType.DEFERRED && (
                        <Lozenge appearance="removed">PAYMENT ERROR</Lozenge>
                      )}
                  </Link>
                  {isDesignIteration1Enabled && (
                    <Text size="small" color="color.text.subtlest">
                      <CopyableText text={billingProfile?.invoiceGroupNumber || billingProfile?.id} />
                    </Text>
                  )}
                </>
              ) : (
                <NoData testId="billing-id-not-found" />
              )
            ),
          };
        case TableColumns.SOLD_TO_ADDRESS:
          return {
            content: wrapInCell(
              <div className="line-clamp">
                <SoldToAddress
                  shipTo={billingProfile?.shipToParty}
                  {...(isDesignIteration1Enabled ? { hasLineBreak: false, shouldDisableLink: true } : {})}
                />
              </div>
            ),
          };
        case TableColumns.PAYMENT_METHOD:
          return {
            content: hasPayOnStatement ? (
              'Pay on account'
            ) : hasPayPalPaymentMethod ? (
              // @ts-ignore
              <PaypalPaymentMethodInTable emailID={billingProfile?.defaultPaymentMethod?.payPal?.email} />
            ) : billingProfile?.defaultPaymentMethod ? (
              formatPaymentMethod(billingProfile?.defaultPaymentMethod)
            ) : (
              <NoData testId="payment-method-not-found" />
            ),
          };
        case TableColumns.CUSTOMER_CONTACTS:
          return {
            content: wrapInCell(
              <EndCustomerContacts
                invoiceGroupId={billingProfile?.id ?? ''}
                transactionAccountId={transactionAccountId}
              />
            ),
          };
        case TableColumns.CURRENCY:
          return {
            content: wrapInCell(billingProfile?.currency ?? <NoData testId="no-currency-found" />),
          };
        case TableColumns.CREATED_ON:
          return {
            content: (
              <>
                <FormattedDate date={billingProfile?.createdAt} format={NEW_DATE_FORMAT} />
              </>
            ),
          };
        case TableColumns.TAX_ID:
          return {
            content: getTaxContent(billingProfile as generatedTypesNew.InvoiceGroup),
          };
        case TableColumns.PRICE_ELIGIBILITY:
          return {
            content: wrapInCell(
              billingProfile?.shipToPriceEligibility ? (
                capitalize(billingProfile?.shipToPriceEligibility)
              ) : (
                <div aria-label="Not applicable">Commercial</div>
              )
            ),
          };
        case TableColumns.NUMBER:
          return {
            content: wrapInCell(
              (billingProfile?.invoiceGroupNumber || billingProfile?.id) ?? <NoData testId="no-number-found" />
            ),
          };
      }
    });
  };

  const initialRows = results.map((billingProfile, index) => {
    const columns = getSelectedColumns(columnsList);

    return {
      key: billingProfile?.id ?? index.toString(),
      cells: rowData(columns, billingProfile),
    };
  });

  const [tableRows, setTableRows] = useState(initialRows);

  return (
    <div className="contact-billing-account-container-v2" data-testid={`search-billing-profiles-table-${testIdSuffix}`}>
      <DynamicResultTable
        beforeTable={label && !shouldHideInnerHeading ? <TableHeading>{label}</TableHeading> : null}
        testId="billing-profiles"
        head={{ cells: getSelectedColumns(columnsList) }}
        rows={tableRows}
        emptyStateProps={isDesignIteration1Enabled ? emptyStatePropForWrongSearch : emptyStateProp}
        isLoading={loading || parentDataLoading}
        totalCount={totalCount}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        onPageChange={onPageChange}
        isPaginationEnabled={showPagination}
        onRowChange={onRowPerPageChange}
        error={error?.message || parentError?.message}
        errorStateProps={errorStateProp}
        hideHeadOnEmptyState={isDesignIteration1Enabled}
        {...(totalCount > 0
          ? {
              sortKey: BillingProfileTableKeys.NAME,
              sortOrder,
              onSort,
            }
          : {})}
      ></DynamicResultTable>
    </div>
  );
};
